import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import HomeHero from '@/components/HomeHero';
import HomeHeroMagazine from '@/components/HomeHeroMagazine';
import HomeSidebar from '@/components/HomeSidebar';
import SubmitNewsSection from '@/components/SubmitNewsSection';
import AdSlot from '@/components/AdSlot';
import SEO from '@/components/SEO';
import { ExplodingPreloader, usePreloaderVariant } from '@/components/BouncingLogoLoader';
import CountyMapWidget from '@/components/CountyMapWidget';
import Style1Block from '@/components/category-blocks/Style1Block';
import Style2Block from '@/components/category-blocks/Style2Block';
import Style3Block from '@/components/category-blocks/Style3Block';
import Style4Block from '@/components/category-blocks/Style4Block';
import FeaturedBlock from '@/components/category-blocks/FeaturedBlock';
import BentoBlock from '@/components/category-blocks/BentoBlock';
import DenseBlock from '@/components/category-blocks/DenseBlock';
import CarouselBlock from '@/components/category-blocks/CarouselBlock';
import ForYouWidget from '@/components/ForYouWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE_MAP_CLASSIC = { style1: Style1Block, style2: Style2Block, style3: Style3Block, style4: Style4Block };
const STYLE_MAP_MAGAZINE = {
  featured: FeaturedBlock,
  bento: BentoBlock,
  dense: DenseBlock,
  carousel: CarouselBlock,
  style1: FeaturedBlock, style2: BentoBlock, style3: DenseBlock, style4: CarouselBlock,
};

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [variant, setVariant] = useState('classic');
  const [loading, setLoading] = useState(true);
  const loaderVariant = usePreloaderVariant();

  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 2;

    async function fetchJson(url) {
      const res = await fetch(url, { cache: 'no-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      return json;
    }

    async function load() {
      try {
        const [hp, lp] = await Promise.all([
          fetchJson(`${API}/public/homepage?lang=${i18n.language}`),
          fetchJson(`${API}/public/landing`).catch(() => ({ variant: 'classic' })),
        ]);
        // Validate homepage payload — if backend returned an error or an empty
        // shell (common right after deploys when DB isn't warm yet), retry.
        if (!hp || !Array.isArray(hp.latest)) throw new Error('invalid homepage payload');
        if (cancelled) return;
        setData(hp);
        setVariant(lp?.variant || 'classic');
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        if (retryCount < maxRetries) {
          retryCount += 1;
          // Exponential backoff: 400ms, 1200ms
          setTimeout(load, 400 * Math.pow(3, retryCount - 1));
        } else {
          setLoading(false);
        }
      }
    }

    setLoading(true);
    load();
    return () => { cancelled = true; };
  }, [i18n.language]);

  const Preloader = (
    <ExplodingPreloader visible={loading} variant={loaderVariant} fullscreen label={loading ? t('common.loading') : null} />
  );

  if (!data && !loading) {
    // Rare: all retries failed. Instead of rendering null (blank page) give
    // the user a clear action + reload option — avoids "no articles showing"
    // after a deploy hiccup.
    return (
      <div className="max-w-xl mx-auto text-center px-4 py-20" data-testid="homepage-empty">
        <h1 className="text-2xl font-bold mb-3 text-gray-900 dark:text-gray-100">Trenutno ne možemo dohvatiti vijesti</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Portal je vjerojatno u fazi osvježavanja. Pokušajte ponovno za trenutak.</p>
        <button onClick={() => window.location.reload()} className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-semibold" data-testid="homepage-reload-btn">Osvježi</button>
      </div>
    );
  }
  if (!data) return Preloader;

  const isMagazine = variant === 'magazine';
  const heroArticles = data.latest.slice(0, isMagazine ? 5 : 13);
  const Hero = isMagazine ? HomeHeroMagazine : HomeHero;
  const STYLE_MAP = isMagazine ? STYLE_MAP_MAGAZINE : STYLE_MAP_CLASSIC;

  const MAG_ROTATION = ['featured', 'bento', 'dense', 'carousel'];
  const pickMagazineStyle = (idx) => MAG_ROTATION[idx % MAG_ROTATION.length];

  return (
    <>
      {Preloader}
      <Helmet><title>E-MINA — Vaš lokalni informativni portal</title></Helmet>
      <SEO title="Vaš lokalni informativni portal"
           description="E-MINA — regionalni portal za Međimurje i Hrvatsku. Najnovije vijesti, politika, sport, kultura, gospodarstvo."
           type="website"
           jsonLd={{
             "@context": "https://schema.org", "@type": "NewsMediaOrganization",
             name: "e-mina.eu", url: typeof window !== 'undefined' ? window.location.origin : '',
             logo: "/logo.png"
           }} />
      <div className="max-w-7xl mx-auto px-4 py-5" data-testid={`homepage-${variant}`} data-variant={variant}>
        {/* Hero grid — full width above the main/sidebar split */}
        <Hero articles={heroArticles} />

        {/* Personalised "Za tebe" feed — only visible to logged-in users
            when the admin toggle is on AND we have at least one match. */}
        <ForYouWidget />

        {/* Main/Sidebar split starts STRAIGHT AFTER the hero, as requested.
            Left column: ad, submit-news CTA, county map, category blocks.
            Right column: the full sidebar (incl. SponsoredPromoWidget). */}
        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8 min-w-0">
            {/* Ad banner (fits within the left column so the sidebar stays aligned) */}
            <div className="flex justify-center" data-testid="ad-placeholder">
              <AdSlot size="970x250" position="homepage-top" />
            </div>

            {/* Predaj svoju vijest */}
            <SubmitNewsSection />

            {/* County map widget */}
            <CountyMapWidget />

            {/* Category sections */}
            <div className="space-y-10">
              {data.categories.filter(s => (s.articles || []).length > 0).map(({ category, articles }, idx) => {
                const style = isMagazine ? pickMagazineStyle(idx) : (category.layout_style || 'style4');
                const Block = STYLE_MAP[style] || (isMagazine ? FeaturedBlock : Style4Block);
                return (
                  <section key={category.id} data-testid={`home-cat-${category.slug}`} data-style={style} data-variant={variant}>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 pl-3"
                          style={{ borderColor: category.color || '#3b82f6' }}>{category.name_hr}</h2>
                      <Link to={`/kategorija/${category.slug}`} className="text-xs text-gray-500 hover:text-red-600 font-medium">{t('home.read_more')} &rarr;</Link>
                    </div>
                    <Block category={category} articles={articles} />
                  </section>
                );
              })}
            </div>
          </div>
          <HomeSidebar mostRead={data.most_read || []} tags={data.tags || []} />
        </div>
      </div>
    </>
  );
}
